import React, { memo } from 'react';
import Markdown, { Components } from 'react-markdown';
import classNames from 'classnames';
import Image from 'antd/lib/image';
import remarkGfm from 'remark-gfm';
import remarkHtml from 'remark-html';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import remarkDirective from 'remark-directive';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import typescript from 'react-syntax-highlighter/dist/cjs/languages/prism/typescript';
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import rangeParser from 'parse-numeric-range';
import { visit } from 'unist-util-visit';
import { LinkOutlined, CopyOutlined } from '@ant-design/icons';
import { message, Button } from 'antd';
import styles from './MarkdownViewer.module.css';
import KemuSnippet from './CustomElements/KemuSnippet/KemuSnippet';
// import { ReactComponent as ApplyCodeIcon } from './apply-code.svg';
import globals from '@common/globals';
import useTranslation from '@hooks/useTranslation';
import routes from '@common/routes';
import { fixRelativeDocsUrl, getDocsBasePath } from '@common/utils';
import { PublicationDocsRelativePathPrefix, STOP_SHORTCUT_PROPAGATION_CLASS } from '@common/constants';

SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('javascript', javascript);

type Props = {
  markdown: string;
  docsUrl: string;
  className?: string;
  /** removes permalink icons and hover effect from headers */
  disablePermalink?: boolean;
  disableSnippets?: boolean;
  /** 
   * allows intercepting link clicks and handling the event as needed
   **/
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>, rawUrl: string | undefined) => void;
  /**
   * Triggered when the KemuSnippet is being dragged
   */
  onDraggingKemuSnippet?: (isDragging: boolean) => void;
  codeBlockFooter?: (code: string) => React.ReactNode;
}

// Update the type to match react-markdown's expectations
type HeaderComponentProps = React.ClassAttributes<HTMLHeadingElement> &
  React.HTMLAttributes<HTMLHeadingElement> & {
    node?: {
      tagName?: string;
    };
  };


/**
 * Creates dynamic headers with permalinks icons and copy to clipboard functionality
 * @param level The level of the header (1-6)
 * @returns A React component that renders a header with a permalink icon and copy to clipboard functionality
 */
const createHeaderComponent = (level: 1 | 2 | 3 | 4 | 5 | 6) => {
  const HeaderComponent = ({ children, ...props }: HeaderComponentProps) => {
    const t = useTranslation('MarkdownViewer');
    // Create a more specific type for the heading element
    const Tag = `h${level}`;
    const Component = Tag as keyof JSX.IntrinsicElements;

    const copyUrl = (id: string) => {
      const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${id}`;
      navigator.clipboard.writeText(url)
        .then(() => message.success(t('LinkCopied')))
        .catch(() => message.error(t('LinkCopyFailed')));
    };

    props.className = classNames(styles.Header);

    // Use createElement instead of JSX to avoid type issues
    return React.createElement(
      Component,
      props,
      <>
        {children}
        {props.id && (
          <a
            href={`#${props.id}`}
            className={styles.HeaderLink}
            aria-hidden="true"
            onClick={(e) => {
              e.preventDefault();
              copyUrl(props.id!);
            }}
          >
            <LinkOutlined />
          </a>
        )}
      </>
    );
  };

  return HeaderComponent;
};

// Create a custom schema based on the default that allows style attributes and video elements
const customSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    // Add 'style' to the list of allowed attributes for all elements
    '*': [...(defaultSchema.attributes?.['*'] || []), 'style', 'className'],
    // Add specific attributes needed for video elements
    video: [
      ...(defaultSchema.attributes?.video || []),
      'src', 'controls', 'autoplay', 'muted', 'loop', 'poster', 'preload', 'width', 'height',
      'playsinline', 'webkit-playsinline', 'autoPlay'
    ],
    // Add attributes for source elements inside video
    source: [
      ...(defaultSchema.attributes?.source || []),
      'src', 'type'
    ],
    img: [
      ...(defaultSchema.attributes?.img || []),
      'src', 'alt', 'width', 'height', 'style', 'className'
    ]
  },
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'div', // Ensure div is allowed for our custom blocks
    'video', // Allow video elements
    'source', // Allow source elements inside video
    'track' // Allow track elements for captions
  ]
};

/**
 * Plugin to transform directives into div elements with specific classes
 * 
 * Processes an 'aligned-block' directive, which is used to set the width and alignment of a block.
 * Usage:
 * ```
 * :::aligned-block{.60.center}
 * This content will be 60% width and centered.
 * :::
 * ```
 */
const remarkCustomBlocks = () => {
  return (tree: any) => {
    visit(tree, (node) => {
      if (
        (node.type === 'containerDirective' ||
         node.type === 'textDirective' ||
         node.type === 'leafDirective') &&
        node.name === 'aligned-block'
      ) {
        // Parse the parameters from node.attributes.className
        let width = null;
        let alignment = null;

        if (node.attributes && node.attributes.class) {
          const parts = node.attributes.class.split(' ');

          // Extract width (if provided)
          if (parts[0] && ['50', '60', '70', '80'].includes(parts[0])) {
            width = parts[0];
          }

          // Extract alignment (if provided)
          if (parts[1] && ['left', 'center', 'right'].includes(parts[1])) {
            alignment = parts[1];
          }
        }

        // Create a string of CSS module class names using classNames
        const blockClass = classNames(
          styles.CustomBlock,
          {
            [styles.width50]: width === '40',
            [styles.width50]: width === '50',
            [styles.width60]: width === '60',
            [styles.width70]: width === '70',
            [styles.width80]: width === '80',
            [styles.width90]: width === '90',
            [styles.width100]: width === '100',
            [styles.alignLeft]: alignment === 'left',
            [styles.alignCenter]: alignment === 'center',
            [styles.alignRight]: alignment === 'right'
          }
        );

        // Convert node to div with our classes
        node.data = {
          ...(node.data || {}),
          hName: 'div',
          hProperties: {
            className: blockClass
          }
        };
      }
    });
  };
};


const MarkdownViewer = (mainProps: Props) => {
  const t = useTranslation('MarkdownViewer');
  const syntaxTheme = oneDark;
  const MarkdownComponents: Components = {
    code ({ node, className, ...props }) {
      const hasLang = /language-(\w+)/.exec(className || '');
      const hasMeta = node?.data?.meta;
      const isKemuSnippet = className === 'language-kemu-snippet';

      if (isKemuSnippet) {
        if (mainProps.disableSnippets) {
          return null;
        }
        
        return <div className={styles.KemuSnippet}>
          <KemuSnippet
            snippetCode={props.children as string}
            onIsDragging={mainProps.onDraggingKemuSnippet}
            docsUrl={mainProps.docsUrl}
          />
        </div>;
      }

      const targetLanguage = hasLang?.[1];
      // Line numbers based on: https://amirardalan.com/blog/syntax-highlight-code-in-markdown
      const applyHighlights: object = (applyHighlights: number) => {
        if (hasMeta) {
          const RE = /{([\d,-]+)}/;
          const metadata = node?.data?.meta?.replace(/\s/g, '');
          if (!metadata) { return {}; }

          const strlineNumbers = RE?.test(metadata)
            ? RE?.exec(metadata)?.[1]
            : '0';
          const highlightLines = rangeParser(strlineNumbers as string);
          const highlight = highlightLines;
          const data = highlight.includes(applyHighlights)
            ? 'highlight'
            : null;
          return { data };
        } else {
          return {};
        }
      };

      return hasLang ? (
        <div className={styles.CodeBlockWrapper}>
          {/* @ts-expect-error ignore */}
          <SyntaxHighlighter
            style={syntaxTheme}
            language={targetLanguage}
            PreTag="div"
            className={mainProps.codeBlockFooter ? styles.CodeComponentWithFooter : 'code-block'}
            showLineNumbers={true}
            wrapLines={!!hasMeta}
            useInlineStyles={true}
            lineProps={applyHighlights}
            customStyle={{
              position: 'relative',
            }}
          >
            {props.children}
          </SyntaxHighlighter>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            className={styles.CopyButton}
            title={t('CopyCode')}
            onClick={() => {
              const originalText = props.children as string;
              navigator.clipboard.writeText(originalText)
                .then(() => message.success(t('CodeCopied')))
                .catch(() => message.error(t('CodeCopyFailed')));
            }}
          />
          {mainProps.codeBlockFooter && (
            <>
              {mainProps.codeBlockFooter(props.children as string)}
            </>
          )}
        </div>
      ) : (
        <code className={className} {...props} />
      );
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    table ({ node, ...props }) {
      return <div className={styles.WrapperTable}><table className={styles.Table} {...props} /></div>;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    video ({ node, ...props }) {
      // Check if the video URL starts with /docs/ and prefix it
      const basePath = props.src ? getDocsBasePath(props.src, mainProps.docsUrl) : mainProps.docsUrl;
      const videoSrc = props.src?.startsWith(PublicationDocsRelativePathPrefix)
        ? `${globals.MARKETPLACE_PUBLIC_URL}/${basePath}/${props.src.replace(PublicationDocsRelativePathPrefix, '')}`
        : props.src;

      // if (props.controls !== true) { props.controls = undefined; }

      return <video className={styles.Video} autoPlay={props.autoPlay || false} {...props} src={videoSrc}>
        {props.children}
      </video>;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    source ({ node, ...props }) {
      const basePath = props.src ? getDocsBasePath(props.src, mainProps.docsUrl) : mainProps.docsUrl;
      const src = props.src?.startsWith(PublicationDocsRelativePathPrefix)
        ? `${globals.MARKETPLACE_PUBLIC_URL}/${basePath}/${props.src.replace(PublicationDocsRelativePathPrefix, '')}`
        : props.src;

      return <source {...props} src={src} />;
    },

    p ({ node, ...props }) {
      // If paragraph contains any image, we don't wrap it in a 'p' tag since
      // custom images (Image component) use 'div' as a wrapper and it's invalid to have a 'div' as a child of a 'p' tag
      if ((node?.children as any[])[0]?.tagName === 'img') {
        // Return just the children without the paragraph wrapper
        return props.children;
      }

      return <p {...props} />;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    img ({ node, ...props }) {
      const src = props.src || '';

      // Check if the image URL starts with /docs/ and prefix it
      // Use the correct base path for widget docs links
      const basePath = src ? getDocsBasePath(src, mainProps.docsUrl) : mainProps.docsUrl;
      const imageSrc = fixRelativeDocsUrl(src, basePath);

      // return <img  {...props} src={imageSrc} alt={props.alt || ''} />;
      return (
        <Image
          preview={{
            mask: t('ClickToEnlarge'),
            classNames: {
              mask: styles.PreviewMask,
              wrapper: styles.PreviewWrap,
            },
          }}
          width={props.width}
          height={props.height}
          src={imageSrc}
          alt={props.alt || ''}
          wrapperClassName={styles.PreviewImage}
          // className={styles.PreviewImage}
        />
      );
    },

    // Generic header component that handles h1-h6
    ...(mainProps.disablePermalink ? {} : {
      h1: createHeaderComponent(1),
      h2: createHeaderComponent(2),
      h3: createHeaderComponent(3),
      h4: createHeaderComponent(4),
      h5: createHeaderComponent(5),
      h6: createHeaderComponent(6),
    }),

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    a: ({ node, ...props }) => {
      let url = props.href;
      // Detect if the link is a relative Kemu file and if so, replace the relative path with the marketplace publication url
      if (props.href?.startsWith(PublicationDocsRelativePathPrefix) && props.href.endsWith('.kemu')) {
        const basePath = props.href ? getDocsBasePath(props.href, mainProps.docsUrl) : mainProps.docsUrl;
        const kemuFileUrl = `${globals.MARKETPLACE_PUBLIC_URL}/${basePath}/${props.href.replace(PublicationDocsRelativePathPrefix, '')}`;
        url = `${routes.recipe.getBrowserRecipeRoute('')}?recipe_url=${kemuFileUrl}`;
      } else if (props.href?.endsWith('.kemu')) {
        // Link to a non-relative Kemu file, redirect to the browser router with a recipe_url param
        url = `${routes.recipe.getBrowserRecipeRoute('')}?recipe_url=${props.href}`;
      }

      const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (mainProps.onLinkClick) {
          mainProps.onLinkClick(event, props.href);
        }
      };


      return (
        <a
          {...props}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
        />
      );
    },
  };

  return (
    <div
      className={STOP_SHORTCUT_PROPAGATION_CLASS}
      tabIndex={0}
      onContextMenu={(e) => {
        // Stop propagation to prevent the canvas handler from intercepting the event
        e.stopPropagation();
      }}
    >
      <Markdown
        className={classNames(styles.MarkdownViewer, mainProps.className)}
        remarkPlugins={[
          remarkGfm,
          remarkHtml,
          remarkDirective,
          remarkCustomBlocks,
        ]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, customSchema],
          rehypeSlug
        ]}
        components={MarkdownComponents}
      >
        {mainProps.markdown}
      </Markdown>
    </div>
  );
};

export default memo(MarkdownViewer);
